// src/pages/Admin/Sidebar.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
//import { useAuth } from '../../context/AuthContext'; // Assuming path to AuthContext
import { usePermissions } from '../../hooks/usePermissions';
import { useAdminStore } from '../../store/adminStore'; // <-- Import the store
import { X } from 'lucide-react'; // Added for mobile close icon

const Sidebar = ({ isOpen, toggleSidebar }) => {
  //const { user } = useAuth();
  //const isSuperAdmin = user?.groups?.includes('super-admin');
  const { hasPermission } = usePermissions();
  const currentLocale = useAdminStore((state) => state.locale); // Read 'context'
  const categoryBasePath = currentLocale ? `categories/manage/${currentLocale}` : 'categories/manage';
  
  const links = [
    hasPermission('users:read-only') && { name: 'Search', path: 'search' },
    hasPermission('users:read-only') && { name: 'Search Prospect', path: 'search-prospect' },
    //{ name: 'Seller Management', path: 'sellers' },
    (hasPermission("users:read-only")) && {
      name: 'My Prospects',
      path: 'prospects',
      children: [
        { name: 'My Referrals', path: 'my-referrals' },  
        { name: 'Prospect Calculator', path: 'prospects/prospect-calculator' },
      ]
    },
    hasPermission('sales:view-leads-for-calling') && { name: 'Prospect Call Leads', path: 'leads/prospect-leads' },
    (hasPermission("admin_user:manage")) && {
      name: 'Prospects Management',
      path: 'prospects',
      children: [
        { name: 'Scrape Tasks', path: 'prospects/scrape-tasks' },
        { name: 'Directory Domains', path: 'prospects/directory-domains' },
        { name: 'Signup Free Responses', path: 'prospects/signup-settings' },
        { name: 'LLM Review Queue', path: 'prospects/llm-review' },
        { name: 'Category Mappings', path: 'prospects/category-mappings' },
      ]
    },
    (hasPermission("admin_user:manage")) && {
      name: 'Notifications',
      path: 'notifications',
      children: [
        { name: 'Manage Content', path: 'notifications/content' },
      ]
    },
    hasPermission("affiliate:view") && {
      name: 'Affiliates & Partners',
      path: 'affiliates',
      children: [
        hasPermission("affiliate:manage") && { name: 'Manage Affiliates', path: 'affiliates/manage' },
        hasPermission("affiliate:manage") && { name: 'Commission Plans', path: 'referrals/commission-plans' },
      ].filter(Boolean)
    },
    hasPermission('coupons:manage') && {
    name: 'Payments',
    path: 'payments',
    children: [
      hasPermission('coupons:view') && { name: 'View Coupons', path: 'payments/view/active' },
      hasPermission("coupons:create-modify") && { name: 'Create / Edit Coupon', path: 'payments/create' },
      hasPermission("coupons:assign-admin-user") && { name: 'Coupon Permissions', path: 'payments/permissions' },
    ].filter(Boolean),
  },
    hasPermission("marketing:landing-pages:manage") && {
      name: 'Marketing',
      path: 'marketing',
      children: [
        //{ name: 'Overview', path: 'marketing' },
        { name: 'Buyer Landing Pages', path: 'marketing/buyer-landing-pages' },
        { name: 'Seller Landing Pages', path: 'marketing/seller-landing-pages' },
      ],
    },
    hasPermission("content:seo:manage") && {
      name: 'SEO Management',
      path: 'seo',
      children: [
        { name: 'Page Manager', path: 'seo/pages' },
        { name: 'Image Uploader', path: 'seo/media-tools' },
        { name: "Category Meta Content", path: "seo/category-meta"},
        { name: "Location Tags", path: "seo/location-tags" },
        { name: "Location Manager", path: "seo/location-manager"},
        { name: "Static Generators", path: "seo/static-tasks" }
      ]
    },
    hasPermission("content:categories:manage") && {
      name: 'Category Management',
      path: 'categories/manage',
      children: [
        { name: 'Manage Categories', path: categoryBasePath },
        { name: 'All Categories', path: `${categoryBasePath}/all-categories` },
        { name: 'Manage Category Groups', path: `${categoryBasePath}/groups` },
        { name: 'Flag Definitions', path: 'categories/flag-definitions' },
      ],
    },
    hasPermission("content:categories:manage") && {
      name: 'Lead Pricing Setup',
      path: 'lead-pricing-setup',
      children: [
        { name: 'Condition Definitions', path: 'lead-pricing-setup/condition-definitions' },
      ],
    },
    hasPermission("cx:update-banned-content") && {       
      name: 'Verification Rules',
      path: 'verification-rules', 
      children: [
        { name: 'Banned Emails', path: 'verification-rules/emails' },
        { name: 'Banned IPs', path: 'verification-rules/ips' },
        { name: 'Banned Phones', path: 'verification-rules/phones' },
        { name: 'Banned Phrases', path: 'verification-rules/phrases' },
      ],
    },
    hasPermission("admin_user:view") && { name: 'Admin Users', path: 'users' },
    hasPermission("admin_user:manage") && { name: 'System Tools', path: 'system-tools' },
  ].filter(Boolean);

  console.log("Final links array:", links);

  // Keeping your original inline styles for the links to preserve exact design
  const getNavLinkStyle = ({ isActive }) => ({
    display: 'block', padding: '8px 0', color: isActive ? '#8A2BE2' : '#333',
    textDecoration: 'none', fontWeight: isActive ? 'bold' : 'normal', fontSize: '1em',
    transition: 'color 0.2s ease-in-out',
  });

  const getChildNavLinkStyle = ({ isActive }) => ({
    ...getNavLinkStyle({ isActive }), padding: '6px 0', fontSize: '0.95em',
    color: isActive ? '#8A2BE2' : '#555',
  });

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-[260px] bg-[#f8f9fa] border-r border-[#e0e0e0] p-[20px] overflow-y-auto transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          isOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between mb-[25px]">
          <h3 className="text-[1.5em] text-[#333] font-semibold m-0">Admin Panel</h3>
          <button 
            onClick={toggleSidebar} 
            className="md:hidden p-1 text-gray-500 hover:text-gray-800 rounded focus:outline-none"
            aria-label="Close sidebar"
          >
            <X size={24} />
          </button>
        </div>
        
        <nav>
          {links.map((link) => (
            <div key={link.name} style={{ marginBottom: '15px' }}>
              {!link.children ? (
                <NavLink 
                  to={`/admin/${link.path}`} 
                  style={getNavLinkStyle}
                  onClick={() => window.innerWidth < 768 && toggleSidebar()}
                >
                  {link.name}
                </NavLink>
              ) : (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '1.05em', color: '#444' }}>{link.name}</div>
                  <div style={{ marginLeft: '15px', borderLeft: '1px solid #ddd', paddingLeft: '10px' }}>
                    {link.children.map((child) => (
                      <div key={child.name} style={{ marginBottom: '8px' }}>
                        <NavLink 
                          to={`/admin/${child.path}`} 
                          style={getChildNavLinkStyle}
                          onClick={() => window.innerWidth < 768 && toggleSidebar()}
                        >
                          {child.name}
                        </NavLink>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
